import { useEffect, useState } from "react";
import { apiBlob } from "../lib/api";
import type { MessageMedia as Media } from "../lib/types";
import { ErrorText } from "./ui";

// useBlobURL loads a file through the API once `load` is true and frees the
// object URL when the message leaves the screen.
function useBlobURL(path: string, load: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (!load) return;
    let alive = true;
    let made: string | null = null;
    apiBlob(path)
      .then((b) => {
        if (!alive) return;
        made = URL.createObjectURL(b);
        setUrl(made);
      })
      .catch((err) => alive && setError(err));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path, load]);
  return { url, error };
}

const kindLabel: Record<string, string> = { image: "Photo", audio: "Voice note", video: "Video", document: "Document", sticker: "Sticker" };

/** MessageMedia shows a WhatsApp photo, voice note, video or document. */
export function MessageMedia({ media, path }: { media: Media; path: string }) {
  // Photos and stickers load straight away; heavier files on request.
  const [load, setLoad] = useState(media.kind === "image" || media.kind === "sticker");
  const { url, error } = useBlobURL(path, load);
  const status =
    media.state === "pending" ? (media.kind === "audio" ? "Transcribing…" : media.kind === "image" ? "Reading the photo…" : "Reading…")
    : media.state === "failed" || media.state === "skipped" ? media.note : null;

  let view: React.ReactNode;
  if (media.kind === "image" || media.kind === "sticker") {
    view = url ? (
      <a href={url} target="_blank" rel="noreferrer"><img className={`msg-image${media.kind === "sticker" ? " sticker" : ""}`} src={url} alt={media.caption || kindLabel[media.kind]} /></a>
    ) : !error && <div className="msg-image placeholder">Loading photo…</div>;
  } else if (media.kind === "audio") {
    view = url ? <audio controls autoPlay src={url} /> : <button type="button" className="secondary small" onClick={() => setLoad(true)} disabled={load}>{load ? "Opening…" : "▶ Play voice note"}</button>;
  } else if (media.kind === "video") {
    view = url ? <video controls src={url} className="msg-video" /> : <button type="button" className="secondary small" onClick={() => setLoad(true)} disabled={load}>{load ? "Opening…" : "▶ Play video"}</button>;
  } else {
    view = url
      ? <a className="button-link small" href={url} download={media.filename || "document"} target="_blank" rel="noreferrer">Open {media.filename || "document"}</a>
      : <button type="button" className="secondary small" onClick={() => setLoad(true)} disabled={load}>{load ? "Opening…" : `📄 ${media.filename || "Open document"}`}</button>;
  }
  return (
    <div className="msg-media">
      {view}
      {status && <small className="muted">{status}</small>}
      {error != null && <ErrorText error={error} />}
    </div>
  );
}
